// @flow

import qs from 'qs';
import Api from './ApiBase';
import { getListParams as getJobsParams } from './jobs.api';

export const getListParams = (params: any) => qs.stringify({
  filter: {
    state: 'online,offline,pending,validating,deactivated',
    ...params.filter
  },
  fields: {
    industries: 'name',
    ...(params.fields || {})
  },
  include: params.include === null ? '' : params.include || 'industry',
  page: {
    ...params.page
  },
  sort: params.sort
});

const getOneParams = (params: any) => qs.stringify({
  include: 'industries,industry,logo_image_item,header_image_item,header_image_item_en,header_image_item_fr,header_image_item_it',
  sort: 'industries.name',
  ...params
});

class CompaniesApi extends Api {
  serializerType = 'companies';

  serializerOptions = {
    attributes: [
      'name', 'url', 'titleDe', 'titleEn', 'titleFr', 'titleIt', 'shortIntroDe', 'shortIntroEn', 'shortIntroFr', 'shortIntroIt',
      'state', 'checked', 'industryId', 'industries', 'mainLocale', 'locales', 'about', 'career', 'careerTabEnabled',
      'contactTabEnabled', 'facebookUrl', 'linkedinUrl', 'instagramUrl',
      'tiktokUrl', 'youtubeUrl', 'uniqUsersCount', 'pageViewsCount', 'showGoogleAnalytics', 'showElasticAnalytics', 'availableJobsTotal',
      'totalJobsCount', 'jobsCount', 'lastMonthJobsCount', 'jobs', 'searchRegions', 'searchIndustries', 'searchDiplomas',
      'searchGraduations', 'searchOffers', 'searchEntryLevels', 'searchEntryAreas', 'searchSpecialities',
      'seoTitleDe', 'seoTitleEn', 'seoTitleFr', 'seoTitleIt'
    ],
    industries: { ref: 'id' },
    searchRegions: { ref: 'id' },
    searchIndustries: { ref: 'id' },
    searchDiplomas: { ref: 'id' },
    searchGraduations: { ref: 'id' },
    searchOffers: { ref: 'id' },
    searchEntryLevels: { ref: 'id' },
    searchEntryAreas: { ref: 'id' },
    searchSpecialities: { ref: 'id' },
    typeForAttribute: (type: string) => {
      switch (type) {
      case 'searchRegions': return 'search_regions';
      case 'searchIndustries': return 'search_industries';
      case 'searchDiplomas': return 'search_diplomas';
      case 'searchGraduations': return 'search_graduations';
      case 'searchOffers': return 'search_offers';
      case 'searchEntryLevels': return 'search_entry_levels';
      case 'searchEntryAreas': return 'search_entry_areas';
      case 'searchSpecialities': return 'search_specialities';
      default: return undefined;
      }
    }
  };

  fetch = async (params: {} = {}) => {
    const json = await this.request(`companies?${getListParams(params)}`);
    const data = await this.deserialize(json);

    return { data, meta: json.meta };
  }

  get = async (id: number | string, params: any) => {
    const json = await this.request(`companies/${id}?${getOneParams(params)}`);
    const data = await this.deserialize(json);

    return { data };
  }

  create = async (company: {}) => {
    const body = await this.serialize(company);
    const json = await this.request(`companies?${getOneParams()}`, { body: JSON.stringify(body), method: 'POST' });
    const data = await this.deserialize(json);

    return data;
  }

  update = async (id: number | string, company: {}, params: any) => {
    const body = await this.serialize({ id, ...company });
    const json = await this.request(`companies/${id}?${getOneParams(params)}`, { body: JSON.stringify(body), method: 'PATCH' });
    const data = await this.deserialize(json);

    return data;
  }

  destroy = async (id: number | string) => {
    await this.request(`companies/${id}`, { method: 'DELETE' });
  }

  fetchJobs = async (id: number | string, params: {} = {}, includedParams: boolean = true) => {
    const json = await this.request(`companies/${id}/jobs?${includedParams ? getJobsParams(params) : qs.stringify(params)}`);
    const data = await this.deserialize(json);

    return { data, meta: json.meta };
  };

  fetchRecruiters = async (companyId: number | string) => {
    const json = await this.request(`companies/${companyId}/recruiters?include=avatar_item&page[limit]=100`);
    const data = await this.deserialize(json);

    return { data };
  }

  fetchBranches = async (companyId: number | string) => {
    const json = await this.request(`companies/${companyId}/branches?sort=position&include=region&page[limit]=1`);
    const data = await this.deserialize(json);

    return { data };
  }

  fetchArticles = async (companyId: number | string, params: {} = {}) => {
    const json = await this.request(`companies/${companyId}/articles?${qs.stringify(params)}`);
    const data = await this.deserialize(json);

    return { data, meta: json.meta };
  }

  fetchEvents = async (companyId: number | string, params: {} = {}) => {
    const json = await this.request(`companies/${companyId}/organized_marketing_events?${qs.stringify(params)}`);
    const data = await this.deserialize(json);

    return { data, meta: json.meta };
  }

  fetchExternalEvents = async (companyId: number | string, params: {} = {}) => {
    const json = await this.request(`companies/${companyId}/organized_external_events?${qs.stringify(params)}`);
    const data = await this.deserialize(json);

    return { data, meta: json.meta };
  }

  fetchAdminCorporateUsers = async (id: number) => {
    const json = await this.request(`companies/${id}/admin_corporate_users?page[limit]=100`);
    const data = await this.deserialize(json);

    return { data, meta: json.meta };
  }
}

export default new CompaniesApi();
